import { Component, OnInit, Optional, Inject, signal, ViewChild, ElementRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AbstractControl, UntypedFormControl, ValidationErrors, Validators } from '@angular/forms';
import { PersonaService } from '../../../../core/services/persona.service';
import { DatePipe } from '@angular/common';
import { ActivityService } from '../../../../core/services/activity.service';

@Component({
  selector: 'app-requisition-share-dialog',
  templateUrl: './requisition-share-dialog.component.html',
  styleUrls: ['./requisition-share-dialog.component.scss'],
  standalone: false,
})
export class RequisitionShareDialogComponent implements OnInit {
  selectedRequisitions: any[] = [];
  emailControl = new UntypedFormControl('');
  hmChipControl = new UntypedFormControl(null, [Validators.required]);
  fromDateControl = new UntypedFormControl('', [Validators.required]);
  toDateControl = new UntypedFormControl('', [Validators.required, (c: AbstractControl) => this.dateRangeValidator(c)]);
  notesControl = new UntypedFormControl('');
  shareToMultiple: boolean = false;
  emailList: string[] = [];
  minDate = new Date();
  dialogTitle: string = 'Share Requisition';
  showResumeCheckbox: boolean = false;
  showVerificationSnapshot = signal(false);
  includeResumeControl = new UntypedFormControl(false);
  includeIwpFeedbackControl = new UntypedFormControl(false);
  includeVerificationSnapshotControl = new UntypedFormControl(false);
  showInterviewList: boolean = false;
  interviewList: any[] = [];
  selectedInterviewIds: any[] = [];
  hiringManagerId: any;
  applicationId: any;
  isLoadingEmail: boolean = false;
  filteredEmp = signal<any[]>([]);
  hiringManagerName: string = '';
  isValidSelection: boolean = false;
  selectedHiringManagers = signal<{ empId: number | null; empName: string; email: string }[]>([]);

  @ViewChild('hmInput') hmInput: ElementRef<HTMLInputElement>;

  constructor(
    public dialogRef: MatDialogRef<RequisitionShareDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private _personaService: PersonaService,
    private datePipe: DatePipe,
    private activityService: ActivityService,
  ) {
    this.selectedRequisitions = data?.selectedArray || [];
    this.selectedInterviewIds = data?.selectedInterviews || [];
    this.dialogTitle = data?.title || 'Share Requisition';
    this.showResumeCheckbox = data?.showResumeCheckbox || false;
    this.showVerificationSnapshot.set(data?.showVerificationSnapshot || false);
    this.showInterviewList = data?.showInterviewList || false;
    this.interviewList = data?.interviewList || [];
    // this.hiringManagerId = data?.hiringManagerId;
    // this.applicationId = data?.applicationId;
  }

  ngOnInit() {
    // If hiring manager ID is provided, fetch the hiring manager email
    if (this.hiringManagerId) {
      this.isLoadingEmail = true;
      this.activityService.getHrDetail(this.hiringManagerId).subscribe(
        (response:any) => {
          if (response && response.email) {
            this.addHiringManager({
              empId: response.empId != null ? parseInt(response.empId) : this.hiringManagerId,
              empName: response.empName || this.hiringManagerName,
              email: response.email,
            });
          }
          this.isLoadingEmail = false;
        },
        // (error) => {
        //   console.error('Error fetching hiring manager details:', error);
        //   this.isLoadingEmail = false;
        // }
      );
    }

    this.fromDateControl.valueChanges.subscribe((val) => {
      const currentToDate = this.toDateControl.value;
      if (val && currentToDate && new Date(currentToDate) <= new Date(val)) {
        this.toDateControl.setValue('');
      }
      this.toDateControl.updateValueAndValidity();
    });

    this.emailControl.valueChanges.subscribe((val) => {
      this.filteredEmp.set([]);
      this.refreshHmValidation();
      if (typeof (val) === 'string' && val.length > 3) {
        this.activityService.getHrDetail(val).subscribe((response) => {
          if (response && response.empId != null) {
            const fullEmail = response.mailId.includes('@') 
              ? response.mailId 
              : response.mailId + '@infosys.com';
            this.filteredEmp.set([{...response, mailId: fullEmail}]);
          }
        });
      }
    });
  }

  refreshHmValidation() {
    if (this.selectedHiringManagers().length > 0) {
      this.hmChipControl.setValue(this.selectedHiringManagers());
      this.hmChipControl.setErrors(null);
    } else {
      this.hmChipControl.setValue(null);
      this.hmChipControl.updateValueAndValidity();
    }
  }

  onHmBlur() {
    this.hmChipControl.markAsTouched();
    this.refreshHmValidation();
  }

  dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const from = this.fromDateControl?.value;
    if (!from || !control.value) {
      return null;
    }
    const max = new Date(from);
    max.setDate(max.getDate() + 7);
    return new Date(control.value) > max ? { dateRange: true } : null;
  }

  get minToDate(): Date {
    if (this.fromDateControl.value) {
      const nextDay = new Date(this.fromDateControl.value);
      nextDay.setDate(nextDay.getDate() + 1);
      return nextDay;
    }
    return this.minDate;
  }

  get maxToDate(): Date | null {
    if (this.fromDateControl.value) {
      const maxDay = new Date(this.fromDateControl.value);
      maxDay.setDate(maxDay.getDate() + 7);
      return maxDay;
    }
    return null;
  }

  get technicalInterviews(): any[] {
    return this.interviewList.filter(
      (interview) => interview.interviewName && interview.interviewName.includes('[Technical]'),
    );
  }

  get isFormValid(): boolean {
    const basicFieldsValid = this.fromDateControl.valid && this.toDateControl.valid && this.selectedHiringManagers().length > 0;
    
    if (this.showResumeCheckbox && this.showInterviewList) {
      const hasResumeSelected = this.includeResumeControl.value;
      const hasIwpFeedbackSelected = this.includeIwpFeedbackControl.value;
      const hasInterviewsSelected = this.selectedInterviewIds.length > 0;
      return basicFieldsValid && (hasResumeSelected || hasIwpFeedbackSelected || hasInterviewsSelected);
    }

    if (this.showResumeCheckbox) {
      return basicFieldsValid && (this.includeResumeControl.value || this.includeIwpFeedbackControl.value);
    }

    return basicFieldsValid;
  }

  addEmail() {
    if (this.emailControl.valid && this.emailControl.value) {
      const email = this.emailControl.value.trim();
      if (!this.emailList.includes(email)) {
        this.emailList.push(email);
        this.emailControl.reset();
      }
    }
  }

  removeEmail(index: number) {
    this.emailList.splice(index, 1);
  }

  onInterviewCheckboxChange(event: any, iwpInterviewId: any) {
    if (event.checked) {
      this.selectedInterviewIds.push(iwpInterviewId);
    } else {
      const index = this.selectedInterviewIds.indexOf(iwpInterviewId);
      if (index > -1) {
        this.selectedInterviewIds.splice(index, 1);
      }
    }
  }

  displayEmpFn(emp: any): string {
    if (!emp) {
      return '';
    }
    return typeof emp === 'string' ? emp : emp.mailId || '';
  }

  onHiringManagerSelected(event: any) {
    const selectedEmp = event.option.value;
    if (selectedEmp) {
      let selectedEmail = selectedEmp.mailId || '';
      if (!selectedEmail.includes('@')) {
        selectedEmail = selectedEmail + '@infosys.com';
      }
      this.addHiringManager({
        empId: selectedEmp.empId ? parseInt(selectedEmp.empId) : null,
        empName: selectedEmp.empName,
        email: selectedEmail,
      });
    }
    this.clearHmInput();
  }

  addHiringManager(hm: { empId: number | null; empName: string; email: string }) {
    const exists = this.selectedHiringManagers().some(
      (m) => m.email === hm.email && m.empId === hm.empId,
    );
    if (!exists) {
      this.selectedHiringManagers.update((list) => [...list, hm]);
    }
    this.refreshHmValidation();
    this.isValidSelection = this.selectedHiringManagers().length > 0;
  }

  removeHiringManager(index: number) {
    this.selectedHiringManagers.update((list) => list.filter((_, i) => i !== index));
    this.refreshHmValidation();
    this.isValidSelection = this.selectedHiringManagers().length > 0;
  }

  clearHmInput() {
    this.emailControl.reset('', { emitEvent: false });
    if (this.hmInput && this.hmInput.nativeElement) {
      this.hmInput.nativeElement.value = '';
    }
    this.filteredEmp.set([]);
  }

  onShare() {

    const shareData = {
      requisitions: this.selectedRequisitions,
      fromDate: this.datePipe.transform(this.fromDateControl.value, 'yyyy-MM-dd'),
      toDate: this.datePipe.transform(this.toDateControl.value, 'yyyy-MM-dd'),
      hiringManagers: this.selectedHiringManagers(),
      notes: this.notesControl.value,
      includeResume: this.includeResumeControl.value,
      includeIwpFeedback: this.includeIwpFeedbackControl.value,
      includeVerificationSnapshot: this.includeVerificationSnapshotControl.value,
    };

    this.dialogRef.close(shareData);
  }

  // onCancel() {
  //   this.dialogRef.close(null);
  // }
}
